/**
 * AASB 133 — Earnings per Share. Rulepack v1.0.0.
 * Recalculates basic and diluted EPS; verifies anti-dilution and the
 * basic ≤ diluted invariant (after considering dilution direction).
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb133-1.0.0';
const fb = findingBuilder('AASB_133' as any, VERSION);

const HMAP = makeHeaderMap([
  ['period', 'period', 'reporting period', 'fy'],
  ['net_profit_ordinary', 'net profit', 'npat ordinary', 'profit attributable to ordinary shareholders', 'profit ordinary', 'net profit ordinary', 'npat'],
  ['weighted_average_shares', 'weighted average shares', 'waos', 'wa ordinary shares', 'shares outstanding'],
  ['dilutive_potential_shares', 'dilutive shares', 'potential ordinary shares', 'options', 'convertibles'],
  ['weighted_average_diluted', 'weighted average diluted shares', 'wads', 'diluted shares'],
  ['basic_eps', 'basic eps', 'eps basic'],
  ['diluted_eps', 'diluted eps', 'eps diluted'],
]);

const REQUIRED = ['net_profit_ordinary', 'weighted_average_shares'];

export function detectAASB133(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  // EPS-related keyword boost
  const boost = ['eps', 'weighted average', 'dilutive'].some(k => headers.some(h => String(h ?? '').toLowerCase().includes(k)));
  return {
    confidence: Math.min(1, (matched.length / REQUIRED.length) * (boost ? 1.5 : 1)),
    matched, missing: REQUIRED.filter(r => !hits.has(r)),
  };
}

export function validateAASB133(headers: string[], data: CellValue[][], _asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_133' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(idx.period !== undefined ? (get('period') ?? `row-${rowNum}`) : `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB133.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const np = parseNumber(get('net_profit_ordinary'));
    const waos = parseNumber(get('weighted_average_shares'));
    const dilutiveShares = idx.dilutive_potential_shares !== undefined ? parseNumber(get('dilutive_potential_shares')) : null;
    const wads = idx.weighted_average_diluted !== undefined ? parseNumber(get('weighted_average_diluted')) : null;
    const basic = idx.basic_eps !== undefined ? parseNumber(get('basic_eps')) : null;
    const diluted = idx.diluted_eps !== undefined ? parseNumber(get('diluted_eps')) : null;

    if (!(waos !== null && waos > 0)) { findings.push(fb.build('AASB133.R02', 'error', 'Weighted average shares must be positive', `Row ${id}: ${waos}.`, { rowId: id })); return; }

    // R03 basic EPS recalc
    const expectedBasic = np! / waos!;
    if (basic !== null) {
      const tolBasic = Math.max(0.0005, Math.abs(expectedBasic) * 0.005);
      if (Math.abs(basic - expectedBasic) > tolBasic) {
        findings.push(fb.build('AASB133.R03', 'error', 'Basic EPS does not reconcile to NP / WASO',
          `Period ${id}: reported basic EPS $${basic.toFixed(4)}, expected $${expectedBasic.toFixed(4)} (NP $${fmt(np!)} / WASO ${fmt(waos!)}).`,
          { rowId: id, evidence: { reported: basic, expected: expectedBasic }, suggestedAdjustment: { field: 'basic_eps', from: basic, to: +expectedBasic.toFixed(6), rationale: 'NP attributable to ordinary shareholders / weighted average ordinary shares.' } }));
      }
    }

    // R04 diluted EPS recalc
    if (dilutiveShares !== null && dilutiveShares > 0) {
      const expectedWADS = wads ?? (waos! + dilutiveShares);
      const expectedDiluted = np! / expectedWADS;
      if (wads !== null && Math.abs(wads - (waos! + dilutiveShares)) > 0.5) {
        findings.push(fb.build('AASB133.R04', 'warning', 'WADS does not match WASO + dilutive shares',
          `Period ${id}: reported WADS ${fmt(wads)}, expected ${fmt(waos! + dilutiveShares)} (WASO + dilutive). May be intentional weighting.`,
          { rowId: id }));
      }
      if (diluted !== null) {
        const tolDil = Math.max(0.0005, Math.abs(expectedDiluted) * 0.005);
        if (Math.abs(diluted - expectedDiluted) > tolDil) {
          findings.push(fb.build('AASB133.R04', 'error', 'Diluted EPS does not reconcile',
            `Period ${id}: reported diluted EPS $${diluted.toFixed(4)}, expected $${expectedDiluted.toFixed(4)} (NP / (WASO + dilutive)).`,
            { rowId: id, evidence: { reported: diluted, expected: expectedDiluted }, suggestedAdjustment: { field: 'diluted_eps', from: diluted, to: +expectedDiluted.toFixed(6), rationale: 'NP / (WASO + dilutive shares).' } }));
        }
      }
    }

    // R05 anti-dilution: when NP is positive, diluted ≤ basic; when NP is negative, anti-dilutive shares should be excluded
    if (basic !== null && diluted !== null) {
      if (np! > 0 && diluted > basic + 0.0001) {
        findings.push(fb.build('AASB133.R05', 'error', 'Diluted EPS > Basic EPS (anti-dilutive treatment error)',
          `Period ${id}: basic $${basic.toFixed(4)}, diluted $${diluted.toFixed(4)}. AASB 133.41 — when potential ordinary shares are anti-dilutive (would increase EPS), they must be excluded from the diluted calc.`,
          { rowId: id }));
      }
      if (np! < 0 && diluted !== basic) {
        findings.push(fb.build('AASB133.R05', 'warning', 'Loss period — diluted EPS should equal basic',
          `Period ${id}: NP $${fmt(np!)} (loss), basic $${basic.toFixed(4)}, diluted $${diluted.toFixed(4)}. AASB 133.43: in loss periods, dilutive instruments are anti-dilutive — diluted = basic.`,
          { rowId: id }));
      }
    }
  });

  return {
    summary: { standard: 'AASB_133' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
